import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import { ImagesProviderProps } from './Types'
import * as PhotoApi from 'unsplash-js/src/methods/photos/types'
import { useQuery } from 'react-query'
import { Photos } from 'unsplash-js/dist/methods/search/types/response'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCreateImageQueryGenerationSync } from 'hooks/queries/imageQueryGenerations/useCreateImageQueryGenerationSync'
import { Basic } from 'unsplash-js/dist/methods/photos/types'
import QueryString from 'qs'

interface IImagesContext {
  images: string[]
  setImages: Dispatch<SetStateAction<string[]>>
  imageKeywords: string[]
  generateImageKeywords: (values: any) => void
  setMaxPhotos: (value: number) => void
  isGenerating: boolean
  abort: () => void
  topic?: string
  abortSignal?: AbortSignal
}

const ImagesContext = createContext<IImagesContext>({
  images: [],
  setImages: () => {},
  setMaxPhotos: () => {},
  imageKeywords: [],
  generateImageKeywords: () => {},
  isGenerating: false,
  topic: undefined,
  abort: () => {},
})

interface IUnsplashImagesContext {
  maxPhotos: number
  photos: PhotoApi.Basic[]
  isLoading: boolean
  retry: (values: any) => void
  setPage: (page: number) => void
  page: number
  isError: boolean
  clearPhotos: () => void
}

const UnsplashImagesContext = createContext<IUnsplashImagesContext>({
  maxPhotos: 0,
  page: 1,
  photos: [],
  isLoading: false,
  retry: () => {},
  isError: false,
  setPage: () => {},
  clearPhotos: () => {},
})

const UnsplashImagesProvider = ({
  children,
  maxPhotos = 4,
  orientation,
}: PropsWithChildren<{ maxPhotos?: number; orientation?: 'portrait' | 'landscape' | 'square' }>) => {
  const { imageKeywords, generateImageKeywords, isGenerating } = useImages()
  const [page, setPage] = useState(1)
  const [photos, setPhotos] = useState<Basic[]>([])
  const unsplashQuery = imageKeywords.slice(0, 15).join(' ')

  const clearPhotos = () => {
    setPhotos([])
  }

  const { isLoading, isError } = useQuery(
    ['unsplashImagesProvider', { unsplashQuery }, page],
    async () => {
      const queryString = QueryString.stringify({
        query: unsplashQuery,
        page,
        per_page: maxPhotos,
        orientation,
      })
      const response = await sendToApi<Photos>(`/unsplash/photos?${queryString}`)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to load photos')
      }
      setPhotos((photos) => photos.concat(response.data.results))
      return response.data
    },
    {
      enabled: !!unsplashQuery,
      refetchOnWindowFocus: false,
    },
  )

  return (
    <UnsplashImagesContext.Provider
      value={{
        maxPhotos,
        photos: photos || [],
        isLoading: isLoading || isGenerating || !unsplashQuery,
        retry: (values) => {
          const { seo_words, ...rest } = values
          generateImageKeywords(rest)
        },
        isError,
        clearPhotos,
        setPage,
        page,
      }}
    >
      {children}
    </UnsplashImagesContext.Provider>
  )
}

export const ImagesProvider = ({
  children,
  initialImages = [],
  initialImageKeywords = [],
  initialMaxPhotos = 4,
  initialTopic,
  orientation,
}: PropsWithChildren<ImagesProviderProps>) => {
  const { mutateAsync, isLoading: isGenerating } = useCreateImageQueryGenerationSync()
  const [images, setImages] = useState<string[]>(initialImages)
  const [topic, setTopic] = useState<string>(initialTopic ?? '')
  const [maxPhotos, setMaxPhotos] = useState<number>(initialMaxPhotos)
  const [imageKeywords, setImageKeywords] = useState<string[]>(initialImageKeywords)
  const abortController = useRef<AbortController | null>(null)
  if (abortController.current === null) abortController.current = new AbortController()

  const generateImageKeywords = useCallback(async ({ topic }: { topic: string }) => {
    try {
      setTopic(topic)
      const generation = await mutateAsync({
        prompt: { topic },
        signal: abortController.current?.signal,
      })
      if (generation.result) {
        setImageKeywords(generation.result.split(' '))
      }
    } catch (e) {
      setImageKeywords([])
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const abort = useCallback(() => {
    abortController.current?.abort()
    abortController.current = new AbortController()
  }, [])

  return (
    <ImagesContext.Provider
      value={{
        images,
        setImages,
        imageKeywords,
        setMaxPhotos,
        generateImageKeywords,
        isGenerating,
        abort,
        topic,
        abortSignal: abortController.current?.signal,
      }}
    >
      <UnsplashImagesProvider maxPhotos={maxPhotos} orientation={orientation}>
        {children}
      </UnsplashImagesProvider>
    </ImagesContext.Provider>
  )
}

export const useImages = () => useContext(ImagesContext)
export const useUnsplashImages = () => useContext(UnsplashImagesContext)
